import { isDesktopShell } from "../../../common/hooks/useIsDesktopView";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  IconSparkles,
  IconBooks,
  IconListCheck,
  IconLayoutSidebarRight,
} from "@tabler/icons-react";
import { SegmentedControl } from "../../ui/SegmentedControl";
import { useApiQuery } from "../../../services/api";
import {
  assistantSourcesRefetchInterval,
  assistantSourcesResolving,
} from "../../../domains/gpt/chatSource";
import { GPT_DESKTOP_COLUMN, GPTConversationWrapper } from "../layout";
import type { ConversationEngine } from "../../../domains/assistant/conversationEngine";
import { cn } from "../../../lib/utils";
import { createMemoryStore, memoryStore } from "../../../lib/store";
import type { GptAction, GptSourceRef } from "../../../domains/gpt/transcript";
import GPTSourcesList from "./GPTSourcesList";
import { ChatSourcesSkeleton } from "../../ui/ChatSources";
import { ChatMessageCard } from "../../ui/ChatMessageCard";
import { DesktopTopNav } from "../../app/desktopTopNav/DesktopTopNav";
import {
  GptContextPanel,
  type GptContextPanelSection,
  type GptPanelMode,
  type GptTraceSummary,
} from "./GptContextPanel";

// One question/answer pair. `content` is the page-rendered turn body (question
// bubble + answer markdown + actions) — the tabs component only frames it,
// observes it for scroll position, and reads `question`/`sources` for the
// Sources view. Keeping `content` opaque leaves all answer rendering with the
// page that owns it.
//
// It is a function of `openSources` rather than a node because the turn body
// carries the "N sources" indicator, and only THIS component owns the tab
// state. Passing the opener down explicitly keeps that coupling typed and
// visible instead of routing it through a context the turn cannot assert on.
export type ConversationTurn = {
  id: string;
  question: string;
  sources: GptSourceRef[];
  // The turn is answered but its sources have not arrived yet. They are emitted
  // last, after their journal and byline have been resolved, so this window is
  // "still coming", never "there are none".
  sourcesPending: boolean;
  // The assistant engine has finished its research and writes the answer: its
  // sources are captured, only their list is still to come.
  isWriting: boolean;
  // Non-null when the turn shows its claims: an Evidence turn
  // (docs/juisci-gpt-evidence-graph.md) or an assistant answer with its check.
  // The page that owns them prebuilds the bodies (this component only frames
  // them, like `content`), plus the counts the tab labels ride. A source answer
  // whose check is still running has no count yet, and its label spins.
  evidence: {
    claimCount: number | null;
    loading: boolean;
    content: React.ReactElement;
  } | null;
  // An Evidence turn's `sources` is empty by construction (the transcript
  // skeleton carries no source parts), so its Sources tab shows the graph's
  // cited sources instead of GPTSourcesList. An assistant answer of the
  // conversation's own run lists every source the run captured, with or without
  // claims. Null otherwise: an answer carried in from a shared conversation lists
  // its sources like a general one. `size` is `row` in the Sources tab, `rail`
  // in the desktop context panel.
  graphSources: {
    count: number;
    content: (size: "row" | "rail") => React.ReactNode;
    // The assistant run whose list resolves its external sources in the
    // background; the tab label spins until it has. Null for an Evidence graph.
    run: { conversationId: string; runId: string } | null;
  } | null;
  // The engine that answered, named by the desktop context panel. Null on the
  // surfaces with no mode, and on a turn not yet answered.
  mode: GptPanelMode | null;
  // What the agent did on this turn, the desktop panel's Progression.
  trace: ReadonlyArray<GptAction>;
  // The line under Progression once the answer has started, null before.
  traceSummary: GptTraceSummary | null;
  // `isContextPanelShown` is true while the desktop context panel is ON
  // SCREEN. It names the mode already, so the badge above the answer gives way
  // to it. Hidden (closed, or nothing to show yet), the badge is the only place
  // the mode shows. `isTraceInPanel` is true once the reader has opened the
  // panel onto its Progression: the live trace above the answer folds then,
  // and stays open as long as the reader has not.
  content: (
    openSources: () => void,
    isContextPanelShown: boolean,
    isTraceInPanel: boolean
  ) => React.ReactNode;
};

type Tab = "answer" | "sources" | "evidence";

// Which tab is open, which Q/R pair is active, and each overlay's scroll
// offset — persisted so the conversation → source-article → back round-trip
// (same SPA runtime, no reload) lands the user exactly where they left off.
type TabsViewState = {
  tab: Tab;
  activeIndex: number;
  sourcesScrollTop: number;
  evidenceScrollTop: number;
};

// Single slot keyed by conversation: only one conversation is ever on screen,
// and reopening a *different* one (key mismatch) must start clean.
const viewStateSlot = memoryStore<{ key: string; state: TabsViewState } | null>(
  null
);

// The desktop context panel's folded sections: a reading preference across
// conversations, for the session. Sources starts open: it is what the answer's
// citation numbers point at.
const useOpenPanelSections = createMemoryStore<
  ReadonlySet<GptContextPanelSection>
>(new Set(["sources"]));

// How long after a click on "N sources" the transcript may still move on its
// own: opening the panel narrows it, and the reflow (and the browser's scroll
// anchoring) shifts the scroll offset in the next frames without the reader.
const PIN_SETTLE_MS = 400;

function readViewState(key: string): TabsViewState | null {
  const current = viewStateSlot.get();
  return current !== null && current.key === key ? current.state : null;
}

// The "band line" under which a pinned question sits: the floating pill on
// mobile, the sticky header on desktop. Pick whichever is actually laid out —
// the other is `display:none` (height 0). `offsetParent` can't tell them apart
// because the mobile pill is `position:fixed` (its offsetParent is null too).
function pickBand(
  pill: HTMLElement | null,
  header: HTMLElement | null
): HTMLElement | null {
  if (pill !== null && pill.getBoundingClientRect().height > 0) return pill;
  if (header !== null && header.getBoundingClientRect().height > 0)
    return header;
  return null;
}

// Breathing room between the transcript's last line and the fixed composer band
// below it, on top of the band's own measured height. The band's padding already
// carries the safe-area reserve, so this is a pure visual gap.
const BAND_CLEARANCE = 24;

// Extra scroll past the band line so the pinned question tucks a touch higher
// (just under the pill) rather than sitting flush against its bottom edge.
const PIN_SCROLL_PAST_BAND = -16;

// The viewport Y of the effective band line a pinned question is aligned to.
function bandLineY(band: HTMLElement): number {
  return band.getBoundingClientRect().bottom - PIN_SCROLL_PAST_BAND;
}

// The conversation viewport with a floating Réponse/Sources(/Preuves) pill.
// The Sources and Evidence tabs show the grounding of the Q/R pair currently
// scrolled into view (their counts ride the pill), frozen on switch. The answer
// list stays mounted (hidden, not unmounted) under the overlays so scroll
// position and any in-flight stream survive the toggle.
//
// The desktop shell has no tabs (Figma "UX/UI Desktop Version"): the answer
// holds the column, and the grounding of the same scrolled-to pair sits beside
// it in the context panel, under a top bar that carries the breadcrumb and the
// panel's toggle. Same tracking, same pinning; only the frame differs.
export default function GPTConversationTabs({
  turns,
  trailing,
  disclaimer,
  leading,
  headerActions,
  aboveTurns,
  footer,
  bandHeight,
  background,
  roomId,
  cwyopPaper,
  engine,
  onOpenSources,
  viewStateKey,
  scrollRef,
  contextPanel,
}: {
  turns: ConversationTurn[];
  trailing: React.ReactNode;
  // The AI disclaimer. Rendered BELOW the pin spacer rather than at the end of
  // the transcript: the spacer is the empty room a pinned question reserves for
  // its answer, and a disclaimer above it would sit stranded in the middle of
  // the screen. Below it, it closes the scroll — resting just above the band.
  disclaimer: React.ReactNode;
  leading: React.ReactNode;
  // The right end of the desktop sticky header row — the conversation's own
  // actions (Share, and the owner's menu). Null on the surfaces that have none.
  headerActions: React.ReactNode;
  // Rendered inside the scroll flow, above the first turn — the conversation's
  // attached files. Inside the flow (not the sticky header) so it scrolls away
  // like the content it belongs to.
  aboveTurns: React.ReactNode;
  // The composer (input bar) — shown only on the answer tab; the Sources view
  // is read-only, so it has no place there. Told whether the desktop context
  // panel is on screen, so the fixed band keeps clear of it.
  footer: (isContextPanelShown: boolean) => React.ReactNode;
  // Live height of that band, measured by its owner. The transcript reserves it
  // at the bottom so its last line clears the band instead of hiding under it.
  bandHeight: number;
  background: string;
  roomId: string | null;
  // Non-null in Chat-with-Paper: the grounding paper renders as the CWYOP card
  // in the sources list with this label.
  cwyopPaper: { articleId: string; label: string } | null;
  // The engine of the conversation, which the sources list's view event reports.
  engine: ConversationEngine;
  // Fired when the user opens the Sources tab (analytics — the act of consulting
  // the grounding, distinct from the list's own view-tracking).
  onOpenSources: () => void;
  // Identifies the conversation for view-state persistence across a round-trip
  // to a source article (conversation id / paper id).
  viewStateKey: string;
  // The ScrollableLayout scroller the transcript lives in. Handed down rather
  // than found by walking up to the first `overflow-y: auto` ancestor: the
  // history drawer locks that scroller to `overflow-y: hidden` while it is
  // open, and a conversation opened from the drawer mounts under exactly that
  // lock. The walk then found no scroller, and the active pair never moved off
  // the latest turn (the pill kept its counts while the reader scrolled up).
  scrollRef: React.RefObject<HTMLDivElement | null>;
  // Non-null in the desktop shell, which frames the conversation with the
  // context panel instead of the tabs. The owner keeps whether it is open, since
  // the composer it also shifts is the owner's.
  contextPanel: { isOpen: boolean; setOpen: (isOpen: boolean) => void } | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const restored = readViewState(viewStateKey);
  // A restored overlay tab is honored only if the CURRENT turns still back it.
  // A rolling-deploy hydrate can come back without the additive
  // `evidenceAnswers` side-car (or, in principle, a transcript without source
  // parts): restoring the overlay then would hide the pill (`showTabs` false)
  // while the composer stays hidden (`tab !== "answer"`), wedging the
  // conversation with no way back. Fall back to the answer view instead.
  const restoredTab: Tab =
    restored === null
      ? "answer"
      : restored.tab === "sources" &&
          !turns.some(
            (turn) =>
              turn.sources.length > 0 ||
              (turn.graphSources !== null && turn.graphSources.count > 0)
          )
        ? "answer"
        : restored.tab === "evidence" &&
            !turns.some((turn) => turn.evidence !== null)
          ? "answer"
          : restored.tab;
  const [tab, setTab] = useState<Tab>(restoredTab);
  // The panel layout has no overlay to be on. A desktop window widened past
  // `xl` switches a conversation from its tabs to the panel, possibly with the
  // Sources tab open: the answer, and its composer, are what shows then.
  const effectiveTab: Tab = contextPanel === null ? tab : "answer";
  const [openSections, setOpenSections] = useOpenPanelSections();
  // The reader opened the panel in this conversation (the top-bar button, or
  // a turn's "N sources"): it then shows before the turn has a source to list.
  const [isOpenedByReader, setOpenedByReader] = useState(false);
  // A reopened conversation lands on its latest turn (the page scrolls there),
  // so that is the active pair until the first measurement says otherwise.
  const [activeIndex, setActiveIndex] = useState(
    restored === null ? Math.max(0, turns.length - 1) : restored.activeIndex
  );
  // Pinning the latest in-session question to the top of the viewport
  // (ChatGPT-style): a trailing spacer is grown so that "question at the top"
  // coincides exactly with "scrolled to the very bottom" — which keeps the
  // jump-to-bottom button hidden while the answer streams into the space below.
  // `pinTargetRef` is the target total scroll height; the spacer holds whatever
  // slack reaches it and shrinks as the answer fills the room. Both stay null/0
  // until the first question asked here — merely reopening a conversation
  // restores to the latest answer (initialToBottom) instead.
  const [spacerHeight, setSpacerHeight] = useState(0);
  const spacerRef = useRef(0);
  const pinTargetRef = useRef<number | null>(null);
  // Index of the turn to pin — read by the scroll layout effect (avoids a
  // `turns` dependency there).
  const pinIndexRef = useRef(0);
  // Bumped after a re-pin so a layout effect can run the smooth scroll once the
  // grown spacer is in the DOM (reading rects before that paint would be stale).
  const [pinScrollTick, setPinScrollTick] = useState(0);

  const contentRef = useRef<HTMLDivElement>(null);
  // The active-pair tracking measures a "band line" at the top of the viewport.
  // On mobile that line is the bottom of the floating pill; on desktop the pill
  // is hidden and the sticky header row stands in. compute() picks whichever is
  // currently laid out (the other has a null offsetParent).
  const pillRef = useRef<HTMLDivElement>(null);
  const desktopHeaderRef = useRef<HTMLDivElement>(null);
  const turnRefs = useRef<Array<HTMLDivElement | null>>([]);
  const rafRef = useRef<number | null>(null);
  const prevTurnCount = useRef(turns.length);
  // Latest-tab mirror for the scroll handler (which closes over the mount-time
  // value). Tracking must freeze while the Sources overlay is open.
  const tabRef = useRef(tab);
  tabRef.current = effectiveTab;
  // Desktop: opening the panel narrows the transcript, and the resize re-runs
  // the tracking, which would hand the panel to whichever earlier turn still
  // ends below the top bar. A pin holds the active turn through that reflow.
  // `until: "scroll"` is the turn whose "N sources" was clicked: the reader
  // chose it, so it holds until the transcript scrolls (wheel, scrollbar,
  // keys, jump-to-bottom alike). `until: "reflow"` is any other opening (the
  // toggle, the panel coming on screen by itself): nobody chose a turn, so the
  // tracking resumes once the reflow is over. Null when nothing is pinned.
  const pinnedAtScrollTopRef = useRef<{
    scrollTop: number;
    settleUntil: number;
    until: "scroll" | "reflow";
  } | null>(null);

  // The Sources overlay scroller + its latest offset, mirrored into the slot so
  // a navigation to a source article and back restores the exact scroll spot.
  const sourcesScrollerRef = useRef<HTMLDivElement>(null);
  const sourcesScrollTopRef = useRef(
    restored === null ? 0 : restored.sourcesScrollTop
  );
  // Set only when restoring with the Sources tab already open: the offset to
  // re-apply once the overlay mounts. Cleared after the first application.
  // Kept with the turn it was saved for: a reader who moves to another turn
  // (the desktop panel follows the scroll) before the overlay opens must see
  // that turn's list from the top, not at another turn's offset.
  const pendingSourcesScrollRef = useRef<{
    scrollTop: number;
    activeIndex: number;
  } | null>(
    restored === null
      ? null
      : {
          scrollTop: restored.sourcesScrollTop,
          activeIndex: restored.activeIndex,
        }
  );
  // The Evidence overlay's mirror of the same machinery (a citation there also
  // round-trips to a source article and must come back to the same spot).
  const evidenceScrollerRef = useRef<HTMLDivElement>(null);
  const evidenceScrollTopRef = useRef(
    restored === null ? 0 : restored.evidenceScrollTop
  );
  const pendingEvidenceScrollRef = useRef<{
    scrollTop: number;
    activeIndex: number;
  } | null>(
    restored === null
      ? null
      : {
          scrollTop: restored.evidenceScrollTop,
          activeIndex: restored.activeIndex,
        }
  );

  // The active pair is the FIRST turn still extending below the band line
  // (`bandLineY` — the same line a pinned question rests on, not the raw pill
  // bottom). Keying off the turn's bottom rather than its top means the moment a
  // turn's answer has scrolled fully above the line, the next turn takes over —
  // even though its question still sits a little below the line. That matches
  // what the user sees: the previous answer is gone and only the new question/
  // answer remain on screen, so its sources should already be the ones shown.
  // Frozen while the Sources overlay is open so the count and the shown turn
  // stay in lockstep with what the user was reading.
  //
  // Measured on scroll AND on any height change of the transcript. Scroll alone
  // is not enough: this effect runs before the page's scroll-to-bottom on a
  // reopen (a child's effect runs before its parent's), and content that lays
  // out later moves the turns without a scroll event. Either way the pill kept
  // the first turn's counts, e.g. "Sources (0)" over an Evidence answer.
  useEffect(() => {
    const content = contentRef.current;
    const scrollRoot = scrollRef.current;
    if (content === null || scrollRoot === null)
      throw new Error("should not happen: the transcript is not mounted");

    // An arrow, not a declaration: a hoisted function would not keep the
    // narrowing of `scrollRoot` above.
    const compute = (): void => {
      if (tabRef.current !== "answer") return;
      const pin = pinnedAtScrollTopRef.current;
      if (pin !== null) {
        // The reflow of the panel opening: follow it, still pinned.
        if (performance.now() < pin.settleUntil) {
          pin.scrollTop = scrollRoot.scrollTop;
          return;
        }
        if (
          pin.until === "scroll" &&
          Math.abs(scrollRoot.scrollTop - pin.scrollTop) < 1
        )
          return;
        pinnedAtScrollTopRef.current = null;
      }
      // pickBand keys off measured height, not offsetParent: the mobile pill is
      // position:fixed so its offsetParent is always null, which would wrongly
      // fall through to the (display:none, rect-bottom ~0) desktop header on
      // mobile and collapse the band line to the very top of the viewport.
      const band = pickBand(pillRef.current, desktopHeaderRef.current);
      if (band === null) return;
      const bandLine = bandLineY(band);
      // First turn whose bottom is still below the band line. Falls back to the
      // last laid-out turn when every turn has scrolled above it (scrolled past
      // the end of the conversation).
      let next: number | null = null;
      for (const [index, el] of turnRefs.current.entries()) {
        if (el === null) continue;
        next = index;
        if (el.getBoundingClientRect().bottom > bandLine) break;
      }
      if (next === null) return;
      setActiveIndex((prev) => (prev === next ? prev : next));
    };

    function scheduleCompute(): void {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        compute();
      });
    }

    // Deferred to the next frame, once the page's initial scroll has landed.
    scheduleCompute();
    scrollRoot.addEventListener("scroll", scheduleCompute, { passive: true });
    const observer = new ResizeObserver(scheduleCompute);
    observer.observe(content);
    return () => {
      observer.disconnect();
      scrollRoot.removeEventListener("scroll", scheduleCompute);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [scrollRef]);

  // A new turn (question sent / answer streaming in) snaps back to the answer
  // view so the user follows the reply, re-anchors the active pair to it, and
  // pins the fresh question to the top of the viewport (see `spacerHeight`).
  useEffect(() => {
    if (turns.length > prevTurnCount.current) {
      setTab("answer");
      // The new turn is what the reader follows now.
      pinnedAtScrollTopRef.current = null;
      const lastIndex = turns.length - 1;
      setActiveIndex(lastIndex);

      const scrollRoot = scrollRef.current;
      if (scrollRoot === null)
        throw new Error("should not happen: the transcript is not mounted");
      const turnEl = turnRefs.current[lastIndex];
      const band = pickBand(pillRef.current, desktopHeaderRef.current);
      if (turnEl !== null && band !== null) {
        const containerTop = scrollRoot.getBoundingClientRect().top;
        const bandOffset = bandLineY(band) - containerTop;
        const turnContentTop =
          turnEl.getBoundingClientRect().top -
          containerTop +
          scrollRoot.scrollTop;
        // The position where the question's top sits on the band line. Growing
        // the spacer so the total scroll height is `target` makes that pinned
        // position coincide with the very bottom — so no jump-to-bottom button.
        const target = turnContentTop + scrollRoot.clientHeight - bandOffset;
        pinTargetRef.current = target;
        const natural = scrollRoot.scrollHeight - spacerRef.current;
        const nextSpacer = Math.max(0, target - natural);
        spacerRef.current = nextSpacer;
        pinIndexRef.current = lastIndex;
        setSpacerHeight(nextSpacer);
        setPinScrollTick((tick) => tick + 1);
      }
    }
    prevTurnCount.current = turns.length;
  }, [turns.length, scrollRef]);

  // Run the smooth scroll AFTER the grown spacer is committed (layout effect,
  // pre-paint). Position by a *relative* delta — bring the question's top onto
  // the band line — rather than scrolling to an absolute max, so a small spacer
  // mismatch can never overshoot the question above the band.
  useLayoutEffect(() => {
    if (pinScrollTick === 0) return;
    const scrollRoot = scrollRef.current;
    if (scrollRoot === null)
      throw new Error("should not happen: the transcript is not mounted");
    const turnEl = turnRefs.current[pinIndexRef.current];
    const band = pickBand(pillRef.current, desktopHeaderRef.current);
    if (turnEl === null || band === null) return;
    const delta = turnEl.getBoundingClientRect().top - bandLineY(band);
    scrollRoot.scrollTo({
      top: scrollRoot.scrollTop + delta,
      behavior: "smooth",
    });
  }, [pinScrollTick, scrollRef]);

  // Keep the spacer holding the pin target as the answer streams in: as content
  // grows the spacer shrinks one-for-one (total height stays at the target), so
  // the question stays put and we stay "at the bottom" until the answer finally
  // overflows the reserved room (then the spacer hits 0 and normal scrolling
  // resumes). Observes the whole content column for any height change.
  useEffect(() => {
    const content = contentRef.current;
    const scrollRoot = scrollRef.current;
    if (content === null || scrollRoot === null)
      throw new Error("should not happen: the transcript is not mounted");
    // An arrow, not a declaration: a hoisted function would not keep the
    // narrowing of `scrollRoot` above.
    const recompute = (): void => {
      if (pinTargetRef.current === null) return;
      const natural = scrollRoot.scrollHeight - spacerRef.current;
      const next = Math.max(0, pinTargetRef.current - natural);
      if (next === spacerRef.current) return;
      spacerRef.current = next;
      setSpacerHeight(next);
    };
    const observer = new ResizeObserver(recompute);
    observer.observe(content);
    return () => {
      observer.disconnect();
    };
  }, [scrollRef]);

  // Mirror the live view state into the slot. Scroll-offset updates ride the
  // ref (the overlay's own scroll handler keeps it fresh); this re-runs on tab
  // / active-pair changes to keep the rest in sync.
  useEffect(() => {
    viewStateSlot.set({
      key: viewStateKey,
      state: {
        tab,
        activeIndex,
        sourcesScrollTop: sourcesScrollTopRef.current,
        evidenceScrollTop: evidenceScrollTopRef.current,
      },
    });
  }, [viewStateKey, tab, activeIndex]);

  // On a restore that lands on the Sources tab, re-apply the saved scroll offset
  // once the overlay has mounted (layout effect — before paint, no flash).
  useLayoutEffect(() => {
    if (effectiveTab !== "sources") return;
    const pending = pendingSourcesScrollRef.current;
    if (pending === null) return;
    pendingSourcesScrollRef.current = null;
    if (pending.activeIndex !== activeIndex) return;
    const el = sourcesScrollerRef.current;
    if (el === null) return;
    el.scrollTop = pending.scrollTop;
  }, [effectiveTab, activeIndex]);

  // Same restore for the Evidence overlay.
  useLayoutEffect(() => {
    if (effectiveTab !== "evidence") return;
    const pending = pendingEvidenceScrollRef.current;
    if (pending === null) return;
    pendingEvidenceScrollRef.current = null;
    if (pending.activeIndex !== activeIndex) return;
    const el = evidenceScrollerRef.current;
    if (el === null) return;
    el.scrollTop = pending.scrollTop;
  }, [effectiveTab, activeIndex]);

  function onSourcesScroll(): void {
    const el = sourcesScrollerRef.current;
    if (el === null) return;
    sourcesScrollTopRef.current = el.scrollTop;
    viewStateSlot.set({
      key: viewStateKey,
      state: {
        tab: "sources",
        activeIndex,
        sourcesScrollTop: el.scrollTop,
        evidenceScrollTop: evidenceScrollTopRef.current,
      },
    });
  }

  function onEvidenceScroll(): void {
    const el = evidenceScrollerRef.current;
    if (el === null) return;
    evidenceScrollTopRef.current = el.scrollTop;
    viewStateSlot.set({
      key: viewStateKey,
      state: {
        tab: "evidence",
        activeIndex,
        sourcesScrollTop: sourcesScrollTopRef.current,
        evidenceScrollTop: el.scrollTop,
      },
    });
  }

  const activeTurn = turns.at(activeIndex);
  const activeSources = activeTurn === undefined ? [] : activeTurn.sources;
  const activeQuestion = activeTurn === undefined ? "" : activeTurn.question;
  const activeSourcesPending =
    activeTurn !== undefined && activeTurn.sourcesPending;
  const isActiveTurnWriting = activeTurn !== undefined && activeTurn.isWriting;
  const activeEvidence = activeTurn === undefined ? null : activeTurn.evidence;
  const activeGraphSources =
    activeTurn === undefined ? null : activeTurn.graphSources;
  // The Sources tab counts a turn's graph sources when it has them (an Evidence
  // turn, or an assistant answer's run list), else its transcript sources.
  const activeSourceCount =
    activeGraphSources !== null
      ? activeGraphSources.count
      : activeSources.length;
  // Reachable while the sources are still coming: that is precisely when the
  // reader wants to know they exist. Disabling it here would hide the wait
  // instead of showing it.
  const sourcesDisabled = activeSourceCount === 0 && !activeSourcesPending;
  // The list's own query, so the label spins exactly while a row shows its
  // skeleton, even when the reader stays on the Answer tab.
  const activeSourcesRun =
    activeGraphSources === null ? null : activeGraphSources.run;
  const activeRunSources = useApiQuery(
    "/assistant/conversations/:conversationId/runs/:runId/sources",
    activeSourcesRun === null
      ? false
      : {
          param: activeSourcesRun,
          refetchInterval: assistantSourcesRefetchInterval,
        }
  );
  const sourcesLoading =
    activeSourcesPending ||
    activeRunSources.state === "pending" ||
    (activeRunSources.state === "success" &&
      assistantSourcesResolving(activeRunSources.body));
  // The Réponse/Sources selector is pointless when nothing in the conversation
  // is grounded (e.g. the onboarding opener) — only show it once at least one
  // turn has sources or an evidence graph.
  const showTabs = turns.some(
    (turn) =>
      turn.sources.length > 0 || turn.evidence !== null || turn.sourcesPending
  );
  // The Evidence segment exists only once a turn shows its claims (an Evidence
  // turn or an assistant passage answer); otherwise the pill keeps two segments.
  const hasEvidenceTurns = turns.some((turn) => turn.evidence !== null);

  // One control, rendered in two places (mobile floating pill / desktop header
  // row) — both share this component's tab state.
  const segmentedControl = (
    <SegmentedControl
      className="pointer-events-auto"
      value={tab}
      onChange={(next) => {
        setTab(next);
        if (next === "sources") onOpenSources();
      }}
      items={[
        {
          value: "answer" as const,
          label: t("Answer"),
          icon: <IconSparkles className="size-4" />,
          disabled: false,
          loading: false,
        },
        {
          value: "sources" as const,
          label: `${t("Sources")} (${String(activeSourceCount)})`,
          icon: <IconBooks className="size-4" />,
          disabled: sourcesDisabled,
          loading: sourcesLoading,
        },
        ...(hasEvidenceTurns
          ? [
              {
                value: "evidence" as const,
                label:
                  activeEvidence === null || activeEvidence.claimCount === null
                    ? t("Evidence")
                    : `${t("Evidence")} (${activeEvidence.claimCount})`,
                icon: <IconListCheck className="size-4" />,
                disabled: activeEvidence === null,
                loading: activeEvidence !== null && activeEvidence.loading,
              },
            ]
          : []),
      ]}
    />
  );

  // Show the grounding OF one turn, not of whichever pair the scroll tracker
  // last landed on: the Sources tab on the phone, the panel's Progression
  // (opening the panel if it was closed) on desktop, since the line clicked
  // counts the searches first (Figma "D2.conversation.reponse.progression").
  function openTurnSources(index: number): void {
    setActiveIndex(index);
    onOpenSources();
    if (contextPanel === null) {
      setTab("sources");
      return;
    }
    pinActiveTurn("scroll");
    contextPanel.setOpen(true);
    setOpenedByReader(true);
    setOpenSections(new Set([...openSections, "progression"]));
  }

  // Opening pins the active turn through its reflow: the layout effect on
  // `isContextPanelShown` does it. Opening unfolds Progression, which is all a
  // turn still being researched has to show, and folds the trace above it.
  function toggleContextPanel(panel: NonNullable<typeof contextPanel>): void {
    if (isContextPanelShown) {
      panel.setOpen(false);
      setOpenedByReader(false);
      return;
    }
    panel.setOpen(true);
    setOpenedByReader(true);
    setOpenSections(new Set([...openSections, "progression"]));
  }

  // Holds the active turn through the reflow of the panel opening (see
  // `pinnedAtScrollTopRef`).
  function pinActiveTurn(until: "scroll" | "reflow"): void {
    const scrollRoot = scrollRef.current;
    if (scrollRoot === null)
      throw new Error("should not happen: the transcript is not mounted");
    pinnedAtScrollTopRef.current = {
      scrollTop: scrollRoot.scrollTop,
      settleUntil: performance.now() + PIN_SETTLE_MS,
      until,
    };
  }

  function toggleSection(section: GptContextPanelSection): void {
    const next = new Set(openSections);
    if (next.has(section)) {
      next.delete(section);
    } else {
      next.add(section);
      // Unfolding Sources is consulting the grounding, like opening the tab.
      if (section === "sources") onOpenSources();
    }
    setOpenSections(next);
  }

  // The panel's Sources body for the active pair: the same three states as the
  // Sources tab, in the panel's narrow row.
  const panelSources =
    activeGraphSources !== null ? (
      activeGraphSources.content("rail")
    ) : activeSourcesPending ? (
      <ChatSourcesSkeleton size="rail" />
    ) : (
      <GPTSourcesList
        sources={activeSources}
        size="rail"
        roomId={roomId}
        cwyopPaper={cwyopPaper}
        engine={engine}
      />
    );

  const panelMode = activeTurn === undefined ? null : activeTurn.mode;
  const panelTrace = activeTurn === undefined ? [] : activeTurn.trace;
  const panelTraceSummary =
    activeTurn === undefined ? null : activeTurn.traceSummary;
  const panelSourcesSection = sourcesDisabled
    ? null
    : {
        // No count while the sources are still coming: "0" would read as none.
        count: activeSourcesPending ? null : activeSourceCount,
        loading: sourcesLoading,
        content: panelSources,
      };
  // A turn still being read has nothing to ground yet (no engine, no trace, no
  // sources): the panel stays off screen until it has, rather than showing an
  // empty card, and the transcript keeps the width meanwhile.
  // Sources still on their way do not count on their own: every streaming
  // turn has them until its citations land, and one that never cites would
  // open the panel on a spinner and narrow the transcript for nothing. A turn
  // the assistant writes has captured its sources: they will land.
  const hasPanelContent =
    panelMode !== null ||
    panelTrace.length > 0 ||
    activeEvidence !== null ||
    activeSourceCount > 0 ||
    isActiveTurnWriting;
  // On its own, the panel comes on screen only once the turn has a source to
  // list: a turn still being researched has the trace above its answer, and a
  // panel that opens onto a spinner narrows the transcript for nothing. The
  // reader can still open it earlier. An assistant turn brings it as soon as
  // it writes, so the transcript narrows before the answer arrives.
  const isContextPanelShown =
    contextPanel !== null &&
    contextPanel.isOpen &&
    hasPanelContent &&
    (isOpenedByReader ||
      activeSourceCount > 0 ||
      activeEvidence !== null ||
      isActiveTurnWriting);

  // The panel also comes on screen by itself: a turn gains its sources, or the
  // reader scrolls from an empty turn onto one with sources. Its reflow must
  // not hand the panel to another turn either, whatever brought it. Before
  // paint, so the pin is set before the resize re-runs the tracking.
  const wasContextPanelShownRef = useRef(isContextPanelShown);
  useLayoutEffect(() => {
    const wasShown = wasContextPanelShownRef.current;
    wasContextPanelShownRef.current = isContextPanelShown;
    // A clicked turn's pin is already set and outlasts this one: keep it.
    if (
      !wasShown &&
      isContextPanelShown &&
      pinnedAtScrollTopRef.current === null
    )
      pinActiveTurn("reflow");
  }, [isContextPanelShown]);

  return (
    <>
      {/* Kept mounted and laid out even on the Sources tab (the opaque overlay
          covers it): preserves scroll position on toggle, and keeps turn rects
          measurable so the active-pair tracking never collapses. */}
      {/* A flex column at least as tall as the scroller's visible area, so the
          transcript's `flex-1` finally has a flex parent to grow into: a short
          conversation still fills the viewport, and the block at its foot can
          be pushed down to the band instead of floating under the last turn. */}
      <div
        ref={contentRef}
        className={cn(
          "page-wrapper flex min-h-full flex-col",
          // The panel is fixed over the right of the scroller: the transcript
          // and its top bar keep to the room left of it, and give it way at
          // the pace it slides in (inside the reflow pin's settle window).
          "motion-safe:transition-[padding] motion-safe:duration-300 motion-safe:ease-out",
          isContextPanelShown && "pr-(--container-gpt-context-panel)"
        )}
      >
        {contextPanel === null ? (
          /* A wide window of the mobile shell: back row (`leading`) and the
             tabs share one sticky header, justify-between, pinned to the top
             with no floating pill or top spacer. Aligned to the reading column
             so the tabs sit on its right edge rather than drifting off to the
             far side of the viewport. */
          <div
            ref={desktopHeaderRef}
            className={cn(
              "sticky top-0 z-30 hidden items-center justify-between gap-4 px-5 py-4 md:flex",
              GPT_DESKTOP_COLUMN
            )}
          >
            <div className="flex items-center">{leading}</div>
            <div className="flex items-center gap-3">
              {showTabs ? segmentedControl : null}
              {headerActions}
            </div>
          </div>
        ) : (
          /* The desktop shell: the page's top bar, the band line the pinned
             question rests on. Sticky through this wrapper, which is what the
             band measurement holds on to. */
          <div ref={desktopHeaderRef} className="sticky top-0 z-30">
            <DesktopTopNav
              context={leading}
              trailing={{
                kind: "actions",
                content: (
                  <>
                    {headerActions}
                    <button
                      type="button"
                      onClick={() => {
                        toggleContextPanel(contextPanel);
                      }}
                      // Nothing to show yet: the panel cannot open onto an
                      // empty card.
                      disabled={!hasPanelContent}
                      aria-pressed={isContextPanelShown}
                      aria-label={
                        isContextPanelShown
                          ? t("Hide the context panel")
                          : t("Show the context panel")
                      }
                      className={cn(
                        "flex size-8 cursor-pointer items-center justify-center rounded-lg border-none p-0 text-ds-slate-800 transition-colors hover:bg-ds-slate-100 disabled:cursor-default disabled:text-ds-slate-400 disabled:hover:bg-transparent",
                        isContextPanelShown
                          ? "bg-ds-slate-100"
                          : "bg-transparent"
                      )}
                    >
                      <IconLayoutSidebarRight className="size-5" stroke={1.5} />
                    </button>
                  </>
                ),
              }}
              surface="default"
              className="static bg-ds-canvas"
            />
          </div>
        )}

        {/* Mobile: clears the floating pill (fixed, outside this scroll flow).
            44 + the wrapper's 32 = 12 of air, the 44px pill, 20 of air (Figma). */}
        {showTabs && contextPanel === null && (
          <div aria-hidden className="h-11 md:hidden" />
        )}

        <GPTConversationWrapper
          className={cn(
            contextPanel === null
              ? "bg-ds-cream md:pt-12"
              : "bg-ds-canvas pt-4",
            GPT_DESKTOP_COLUMN
          )}
          style={{
            paddingBottom: `${bandHeight + BAND_CLEARANCE}px`,
          }}
        >
          {aboveTurns}
          {turns.map((turn, index) => (
            <div
              key={turn.id}
              ref={(el) => {
                turnRefs.current[index] = el;
              }}
              className="flex flex-col gap-6"
            >
              {turn.content(
                () => {
                  openTurnSources(index);
                },
                isContextPanelShown,
                isContextPanelShown && isOpenedByReader
              )}
            </div>
          ))}
          {trailing}
          {/* Pin spacer: holds the slack that makes "question at the top"
              coincide with "scrolled to the bottom" (see `spacerHeight`) — with
              the disclaimer riding under it, so the reserved room stays empty
              and the disclaimer closes the scroll just above the band. One flex
              child, so the column's gap counts once whatever the spacer holds.
              `mt-auto` drops the pair to the foot of the column while the
              conversation is shorter than the viewport (the disclaimer rests on
              the band rather than under the last turn); once the content fills
              the column there is no free space left and the margin collapses. */}
          <div className="mt-auto flex flex-col">
            <div aria-hidden style={{ height: `${spacerHeight}px` }} />
            {disclaimer}
          </div>
        </GPTConversationWrapper>
      </div>

      {/* The desktop context panel, fixed against the content area (the
          desktop shell's transformed box) at the right of the conversation. */}
      {isContextPanelShown && (
        <aside className="fixed inset-y-0 right-0 z-30 flex w-gpt-context-panel animate-context-panel-in flex-col">
          <GptContextPanel
            mode={panelMode}
            trace={panelTrace}
            traceSummary={panelTraceSummary}
            evidence={
              activeEvidence === null
                ? null
                : {
                    loading: activeEvidence.loading,
                    content: activeEvidence.content,
                  }
            }
            sources={panelSourcesSection}
            openSections={openSections}
            onToggleSection={toggleSection}
          />
        </aside>
      )}

      {/* Mobile: the floating centered pill (desktop uses the sticky header).
          Hung 12px under the header's MEASURED height (safe-area included), so a
          taller header row never collides with it. */}
      {showTabs && contextPanel === null && (
        <div
          ref={pillRef}
          className="fixed inset-x-0 z-30 flex justify-center pointer-events-none pt-3 md:hidden"
          style={{ top: "var(--scrollable-header-height)" }}
        >
          {segmentedControl}
        </div>
      )}

      {effectiveTab === "sources" && showTabs && (
        <div
          // The opaque background lives on this fixed, non-scrolling frame — not
          // on the scroller. On iOS the rubber-band over-scroll paints on the
          // scrolled layer, so a background set on the scroller itself decouples
          // at the edges and lets the answer view peek through; keeping it on a
          // static frame means the bounce only ever reveals this background.
          className="fixed inset-x-0 bottom-0 z-10"
          style={{ top: "var(--safe-area-top)", background }}
        >
          <div
            ref={sourcesScrollerRef}
            onScroll={onSourcesScroll}
            // `overscroll-contain`: the rubber-band over-scroll must not chain to
            // the answer view behind it (which would peek out at the top).
            className="h-full overflow-y-auto overscroll-contain"
          >
            <div
              className={cn(
                "flex flex-col gap-4 px-5 pt-32 pb-[calc(48px+var(--safe-area-bottom))]",
                GPT_DESKTOP_COLUMN
              )}
            >
              {/* The overlay head is the SAME user bubble the Answer tab
                  shows, so the asked question reads identically on every tab. */}
              <ChatMessageCard
                variant={isDesktopShell() ? "desktop" : "default"}
              >
                {activeQuestion}
              </ChatMessageCard>
              <hr className="m-0 border-t border-ds-mint-800/15" />
              {/* An Evidence turn's grounding is its graph's cited sources
                  (prebuilt by the page); a general turn's is the transcript
                  sources rendered by GPTSourcesList. */}
              {activeGraphSources !== null ? (
                activeGraphSources.content("row")
              ) : activeSourcesPending ? (
                // Answered, sources still on their way: the server resolves
                // each citation's journal and byline before emitting them, so
                // this is a wait, not an absence. Rendering the list here would
                // read "No sources for this answer." for a second or two.
                <ChatSourcesSkeleton size="row" />
              ) : (
                <GPTSourcesList
                  sources={activeSources}
                  size="row"
                  roomId={roomId}
                  cwyopPaper={cwyopPaper}
                  engine={engine}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* The Evidence overlay — the same fixed frame + scroller as Sources,
          showing the active Evidence turn's gated graph (the page prebuilds the
          body; this component only frames it). */}
      {effectiveTab === "evidence" && showTabs && (
        <div
          className="fixed inset-x-0 bottom-0 z-10"
          style={{ top: "var(--safe-area-top)", background }}
        >
          <div
            ref={evidenceScrollerRef}
            onScroll={onEvidenceScroll}
            className="h-full overflow-y-auto overscroll-contain"
          >
            <div
              className={cn(
                "flex flex-col gap-4 px-5 pt-32 pb-[calc(48px+var(--safe-area-bottom))]",
                GPT_DESKTOP_COLUMN
              )}
            >
              {/* The overlay head is the SAME user bubble the Answer tab
                  shows, so the asked question reads identically on every tab. */}
              <ChatMessageCard
                variant={isDesktopShell() ? "desktop" : "default"}
              >
                {activeQuestion}
              </ChatMessageCard>
              <hr className="m-0 border-t border-ds-mint-800/15" />
              {activeEvidence === null ? null : activeEvidence.content}
            </div>
          </div>
        </div>
      )}

      {/* Composer: answer tab only — the overlay views are read-only. */}
      {effectiveTab === "answer" && footer(isContextPanelShown)}
    </>
  );
}
